import { unsafeFromArray } from "lib";
import React from "react";
import { defineMessage, useIntl } from "react-intl";
import { LineChart } from "../charts/LineChart";
import ColorLegend from "../charts/lib/ColorLegend";
import { withFigureIO } from "../components/figure";
import { useConfig } from "../config";
import { metadata } from "../data/data_finance_aid_fig_ODA_GDP";
import { datasets } from "../data/data_finance_aid_fig_ODA_GDP_datasets";
import { getAidFlowName, mkGemDataDecoder, useCheckboxState, useFigureState, useMultiSelectState } from "../domain";
import { useMedia, useOrderedColorScale } from "../hooks";
import * as M from "../materials";
import { breakpoints } from "../materials/consts.stylex";
import { An, Ar, Eq, io, O, Ord, pipe } from "../prelude";
export * from "../data/data_finance_aid_fig_ODA_GDP";
const DEFAULT_DONORS = [
    "United States",
    "United Kingdom",
    "Belgium",
    "Switzerland",
    "Netherlands (Kingdom of the)"
];
export const Data = mkGemDataDecoder([
    "value",
    "year",
    "ind_id"
], {
    donor: io.string,
    type: io.union([
        io.literal("average"),
        io.literal("entity")
    ])
});
export const Chart = ({ data: dataRaw })=>{
    const intl = useIntl();
    const env = useConfig();
    const screenMDown = !useMedia(breakpoints.m);
    const { aidflows } = useConfig();
    const [, actions] = useFigureState();
    const [selectedDonors] = useMultiSelectState("donors");
    const [showComparison] = useCheckboxState("toggleComparison");
    const { entityData, averageData } = React.useMemo(()=>{
        const data = dataRaw.map((x)=>({
                ...x,
                donorId: x.donor,
                donor: getAidFlowName(aidflows, "donor", x.donor)
            }));
        const entityData = data.filter((x)=>x.type === "entity");
        const averageData = data.filter((x)=>x.type === "average");
        return {
            entityData,
            averageData
        };
    }, [
        dataRaw,
        aidflows
    ]);
    const donorKeys = React.useMemo(()=>pipe(entityData, Ar.uniq(Eq.fromEquals((a, b)=>a.donorId === b.donorId)), unsafeFromArray, An.sort(Ord.contramap((x)=>x.donor)(Ord.ordString))), [
        entityData
    ]);
    React.useEffect(()=>{
        actions.setSelectionControl("donors", {
            type: "MultiSelect",
            label: intl.formatMessage(defineMessage({
                id: "Donors",
                defaultMessage: "Donors"
            })),
            selected: pipe(donorKeys, An.map((x)=>x.donorId), An.filter((x)=>DEFAULT_DONORS.includes(x)), O.chain(An.fromArray))
        }, donorKeys.map((x)=>({
                value: x.donorId,
                label: x.donor
            })));
        actions.setToggleControl("toggleComparison", {
            type: "Checkbox",
            label: intl.formatMessage(defineMessage({
                id: "Show donor groups",
                defaultMessage: "Show donor groups"
            })),
            selected: false
        });
    }, [
        intl,
        actions,
        env.countries,
        donorKeys
    ]);
    const chartData = React.useMemo(()=>{
        const entities = pipe(selectedDonors, O.fold(()=>[], (selected)=>entityData.filter((x)=>selected.includes(x.donorId)).concat(showComparison ? averageData : [])));
        return entities;
    }, [
        entityData,
        selectedDonors,
        showComparison,
        averageData
    ]);
    const selectedCountryLabels = React.useMemo(()=>{
        return unsafeFromArray(pipe(selectedDonors, O.map((selected)=>selected.map((id)=>entityData.find((x)=>x.donorId === id))), O.fold(()=>[], (entities)=>entities.map((x)=>x.donor))));
    }, [
        entityData,
        selectedDonors
    ]);
    const { colorScale, colorLegendValues } = useOrderedColorScale(O.some(selectedCountryLabels));
    return <>
      <LineChart tLabel={(s)=>s} height={screenMDown ? M.chartHeight.s : M.chartHeight.m} x="year" yAnnotations={[
        {
            type: "line",
            value: 0.007,
            label: "Benchmark"
        }
    ]} timeParse="%Y" timeFormat="%Y" numberFormat=".2%" yTicks={[
        0,
        0.002,
        0.004,
        0.006,
        0.008,
        0.01,
        0.012,
        0.014
    ]} category="datum.donor" colorLegend={false} colorScale={colorScale} values={chartData} markStyle={(x)=>x.type === "average" ? "muted" : "normal"} connectLines showTooltip makeSections={(line)=>[
            {
                line: line.filter((x)=>x.datum.year <= 2025),
                style: {}
            },
            {
                line: line.filter((x)=>x.datum.year >= 2025),
                style: {
                    strokeDasharray: "0 6"
                }
            }
        ]}/>
      <ColorLegend inline maxWidth={"100%"} values={colorLegendValues}/>
    </>;
};
export default withFigureIO({
    url: new URL("../data/data_finance_aid_fig_ODA_GDP.json", import.meta.url).href,
    csv: new URL("../data/data_finance_aid_fig_ODA_GDP.zip", import.meta.url).href,
    xlsx: new URL("../data/data_finance_aid_fig_ODA_GDP.xlsx", import.meta.url).href,
    metadata,
    Data,
    Chart,
    size: "main",
    datasets
});
